// src/lib/packSize.ts
// Pack-tier SKUs encode the pack size in the NAME ("น้ำปลาหวาน 2 กระปุก 200฿",
// "ครีมใบบัวบก U-RA 3 กระปุก") while unitCount stays 1 — the DB has no
// structured "this SKU = 3 jars" signal, and shop owners rarely set unitCount.
//
// Anything that has to reason in PIECES rather than sell-units needs this:
// the brain prompt (so a bare "เอา 2" isn't multiplied by the pack), the brain
// executor (qty reconcile), and the giveaway tiers (ซื้อ 3 กระปุก แถม 2 —
// buying one "3 กระปุก" variant is 3 pieces, not 1).

// แกลลอน/แกลอน/ลอน: ร้านปุ๋ยพาราโกลด์ตั้งขั้นราคาเป็นตัวเลือก "(2แกลอน) 780 / (3 แกลลอน) 980"
// unitCount 1 — กติการาคาตามจำนวน (lib/priceRules.ts) ต้องนับตัวเลือกนี้เป็น 2/3 หน่วย
// ไม่ใช่ 1 (เคสจริง 8 ก.ย. 2026: "2 แกลลอน" + "1 แกลอน" = 3 หน่วย = ชุด 980)
// ⚠️ ไม่รวม "ลิตร" — "ขนาด 1 ลิตร (2แกลอน)" ลิตรคือขนาดขวด ไม่ใช่จำนวน
// ต้น: ร้านต้นกล้าผักตั้งตัวเลือก "10ต้น / 20ต้น / 100ต้น" — ไม่นับเป็นแพ็ค AI ใส่
// "กระเจี๊ยบ 20ต้น" เป็น quantity:20 แล้วตัวกันจำนวนซ้อนใน executor ข้ามไป
// (เคสจริง ORD-MU0V7IQL 14 ก.ย. 2026: 119฿ กลายเป็น 2,380฿ ยอดโอน 2,659)
// ⚠️ ตัวเลขหลัง "/" ไม่นับ — ออกัสซีฟู้ด "47/50 ตัว 190฿/กิโลกรัม" คือไซส์กุ้ง (ตัวต่อกิโล)
// ไม่ใช่แพ็ค 50 ตัว · ตรวจกับชื่อสินค้า/ตัวเลือกทั้ง prod แล้ว เปลี่ยนเฉพาะต้นกล้าผัก
const PACK_SIZE_RE = /(?<![\d/])(\d{1,3})\s*(?:กระปุก|ขวด|ชิ้น|แพ็ค|แพ็ก|ห่อ|กล่อง|ถุง|ซอง|ชุด|อัน|ก้อน|แกลลอน|แกลอน|ลอน|ต้น|ตัว)/;

/** Pack size baked into a SKU name (≥2), or null. "จำนวน N" counts too. */
export function parsePackSize(name: string): number | null {
  const m = name.match(PACK_SIZE_RE);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n >= 2 ? n : null;
}

/**
 * Pieces in one sold unit of a SKU. Prefers the structured `unitCount` (the
 * correct setup) and falls back to the count baked into the name. Variant wins
 * over product — a "3 กระปุก" variant of a plain-named product is 3 pieces.
 */
export function resolvePackSize(
  product: { name?: string | null; unitCount?: number | null } | null | undefined,
  variant?: { name?: string | null; unitCount?: number | null } | null,
): number {
  if (variant) {
    if ((variant.unitCount ?? 1) > 1) return Number(variant.unitCount);
    const fromName = variant.name ? parsePackSize(variant.name) : null;
    if (fromName) return fromName;
  }
  if ((product?.unitCount ?? 1) > 1) return Number(product!.unitCount);
  const fromName = product?.name ? parsePackSize(product.name) : null;
  return fromName ?? 1;
}
